import { z } from 'zod';

// Contingency + alerts (setter spec §10.8, §10.9; UAT-46/47). An AI kill switch stops LIA's automatic attention for a
// scope (global · workspace · project) and optionally one channel; nothing is deleted or marked done — skipped work is
// recorded as «suspendida por contingencia» and reconciled at a controlled, audited resume. Operational incidents are
// alerted to Estateflow (Slack) and to the developer's designated owner (WhatsApp) with dedupe and cool-down.

const Iso = z.string();
const Id = z.string().min(1).max(64);

export const CONTINGENCY_SUSPENDED_LABEL = 'Suspendida por contingencia';

export const CONTINGENCY_SCOPES = ['global', 'workspace', 'project'] as const;
export const ContingencyScopeSchema = z.enum(CONTINGENCY_SCOPES);
export type ContingencyScope = z.infer<typeof ContingencyScopeSchema>;
export const CONTINGENCY_SCOPE_LABELS: Record<ContingencyScope, string> = { global: 'Toda la plataforma', workspace: 'Todo el espacio', project: 'Proyecto' };

export const CONTINGENCY_CHANNELS = ['whatsapp', 'voice'] as const;
export const ContingencyChannelSchema = z.enum(CONTINGENCY_CHANNELS);
export type ContingencyChannel = z.infer<typeof ContingencyChannelSchema>;
export const CONTINGENCY_CHANNEL_LABELS: Record<ContingencyChannel | 'all', string> = { all: 'Todos los canales', whatsapp: 'WhatsApp', voice: 'Llamadas IA' };

/** LIA state reported in alerts (§10.9). */
export const LIA_STATES = ['operando', 'degradada', 'pausada'] as const;
export const LiaStateSchema = z.enum(LIA_STATES);
export type LiaState = z.infer<typeof LiaStateSchema>;
export const LIA_STATE_LABELS: Record<LiaState, string> = { operando: 'Operando', degradada: 'Degradada', pausada: 'Pausada' };

export const ContingencyStopSchema = z.object({
  id: Id,
  scope: ContingencyScopeSchema,
  workspaceId: Id.nullable(),
  projectId: Id.nullable(),
  projectName: z.string().nullable(),
  channel: ContingencyChannelSchema.nullable(),
  reason: z.string(),
  active: z.boolean(),
  activatedAt: Iso,
  activatedBy: z.string().nullable(),
  resumedAt: Iso.nullable(),
  resumedBy: z.string().nullable(),
  resumeReason: z.string().nullable(),
  resumeDecision: z.record(z.string(), z.unknown()).nullable(),
  incidentId: Id.nullable(),
  /** Whether the caller may resume it (a workspace admin cannot resume a global stop). */
  canResume: z.boolean(),
});
export type ContingencyStop = z.infer<typeof ContingencyStopSchema>;

export const ActivateContingencySchema = z.object({
  /** null/omitted = the whole workspace. */
  projectId: Id.nullable().optional(),
  /** null/omitted = every channel. */
  channel: ContingencyChannelSchema.nullable().optional(),
  reason: z.string().trim().min(5).max(500),
});
export type ActivateContingency = z.infer<typeof ActivateContingencySchema>;
export const ActivateGlobalContingencySchema = ActivateContingencySchema.omit({ projectId: true });

// ───────── Controlled resume (§10.8, UAT-47) ─────────

export const RESUME_CATEGORIES = ['reminders', 'steps', 'inbounds'] as const;
export const ResumeCategorySchema = z.enum(RESUME_CATEGORIES);
export type ResumeCategory = z.infer<typeof ResumeCategorySchema>;
export const RESUME_CATEGORY_LABELS: Record<ResumeCategory, string> = {
  reminders: 'Recordatorios de cita',
  steps: 'Pasos de cadencias y rutinas',
  inbounds: 'Mensajes entrantes sin respuesta',
};

export const RESUME_CHOICES = ['skip', 'run_now', 'handoff'] as const;
export const ResumeChoiceSchema = z.enum(RESUME_CHOICES);
export type ResumeChoice = z.infer<typeof ResumeChoiceSchema>;
export const RESUME_CHOICE_LABELS: Record<ResumeChoice, string> = {
  skip: 'Omitir (queda registrado, no se envía)',
  run_now: 'Ejecutar ahora (solo lo que sigue vigente)',
  handoff: 'Pasar a un asesor (tarea humana)',
};

export const ResumeItemSchema = z.object({
  id: Id,
  category: ResumeCategorySchema,
  contactId: Id.nullable(),
  contactName: z.string().nullable(),
  projectName: z.string().nullable(),
  /** When it was due (reminder/step) or received (inbound). */
  at: Iso.nullable(),
  label: z.string(),
  /** No longer valid to run (e.g. the visit already happened); always skipped. */
  stale: z.boolean(),
  /** A human owns the opportunity (final handoff, §05.23): never resumed by LIA. */
  humanOwned: z.boolean(),
});
export type ResumeItem = z.infer<typeof ResumeItemSchema>;

export const ResumeCategorySummarySchema = z.object({ total: z.number().int(), stale: z.number().int(), humanOwned: z.number().int(), runnable: z.number().int() });
export const ResumePreviewSchema = z.object({
  stopId: Id,
  categories: z.record(ResumeCategorySchema, ResumeCategorySummarySchema),
  /** Up to 50 items per category, oldest first. */
  items: z.array(ResumeItemSchema),
  /** Campaigns that were auto-paused by the stop (resumed manually from Campañas). */
  pausedCampaigns: z.number().int(),
  /** Other stops still active that will keep work suspended after this resume. */
  otherActiveStops: z.number().int(),
});
export type ResumePreview = z.infer<typeof ResumePreviewSchema>;

export const ResumeContingencySchema = z.object({
  reason: z.string().trim().min(5).max(1000),
  /** §10.8: only resume after the problem was corrected and verified. */
  verified: z.literal(true),
  choices: z.object({ reminders: ResumeChoiceSchema.default('skip'), steps: ResumeChoiceSchema.default('skip'), inbounds: ResumeChoiceSchema.default('handoff') }),
});
export type ResumeContingency = z.input<typeof ResumeContingencySchema>;

export const ResumeResultSchema = z.object({
  stop: ContingencyStopSchema,
  results: z.record(ResumeCategorySchema, z.object({ ran: z.number().int(), skipped: z.number().int(), handedOff: z.number().int(), keptHuman: z.number().int(), stillSuspended: z.number().int() })),
});
export type ResumeResult = z.infer<typeof ResumeResultSchema>;

// ───────── Operational incidents + alerts (§10.9) ─────────

export const OPS_INCIDENT_KINDS = ['kill_switch', 'crm_writeback_failures', 'whatsapp_send_failures', 'booking_errors'] as const;
export const OpsIncidentKindSchema = z.enum(OPS_INCIDENT_KINDS);
export type OpsIncidentKind = z.infer<typeof OpsIncidentKindSchema>;
export const OPS_INCIDENT_KIND_LABELS: Record<OpsIncidentKind, string> = {
  kill_switch: 'Interruptor de emergencia',
  crm_writeback_failures: 'Fallas al escribir en el CRM',
  whatsapp_send_failures: 'Fallas de envío de WhatsApp',
  booking_errors: 'Errores del proveedor de citas',
};

export const OPS_SEVERITIES = ['critical', 'major', 'minor'] as const;
export const OpsSeveritySchema = z.enum(OPS_SEVERITIES);
export type OpsSeverity = z.infer<typeof OpsSeveritySchema>;
export const OPS_SEVERITY_LABELS: Record<OpsSeverity, string> = { critical: 'Crítica', major: 'Alta', minor: 'Menor' };

export const OPS_ALERT_STATUS_LABELS: Record<'pending' | 'sent' | 'failed' | 'skipped', string> = { pending: 'En cola', sent: 'Enviada (sin verificar)', failed: 'Falló', skipped: 'No enviada' };

export const OpsAlertDeliverySchema = z.object({
  id: Id,
  channel: z.enum(['slack', 'whatsapp']),
  kind: z.string(),
  targetLabel: z.string(),
  status: z.enum(['pending', 'sent', 'failed', 'skipped']),
  detail: z.string().nullable(),
  createdAt: Iso,
  sentAt: Iso.nullable(),
});
export type OpsAlertDelivery = z.infer<typeof OpsAlertDeliverySchema>;

export const OpsIncidentSchema = z.object({
  id: Id,
  code: z.string(),
  workspaceId: Id.nullable(),
  projectId: Id.nullable(),
  projectName: z.string().nullable(),
  channel: z.string().nullable(),
  kind: OpsIncidentKindSchema,
  severity: OpsSeveritySchema,
  status: z.enum(['open', 'resolved']),
  title: z.string(),
  impact: z.string(),
  liaState: LiaStateSchema,
  containment: z.string().nullable(),
  owner: z.string().nullable(),
  needsHumanAction: z.boolean(),
  occurrences: z.number().int(),
  firstSeenAt: Iso,
  lastSeenAt: Iso,
  resolvedAt: Iso.nullable(),
  resolution: z.string().nullable(),
  pendingAfter: z.string().nullable(),
  deliveries: z.array(OpsAlertDeliverySchema),
});
export type OpsIncident = z.infer<typeof OpsIncidentSchema>;

export const OpsIncidentQuerySchema = z.object({ status: z.enum(['open', 'resolved', 'all']).default('all'), limit: z.coerce.number().int().min(1).max(200).default(50) });
export const ResolveOpsIncidentSchema = z.object({
  resolution: z.string().trim().min(5).max(1000),
  /** What remains to reconcile or attend (§10.9 recovery). */
  pendingAfter: z.string().trim().max(1000).nullable().optional(),
});

export const ContingencyOverviewSchema = z.object({
  liaState: LiaStateSchema,
  active: z.array(ContingencyStopSchema),
  history: z.array(ContingencyStopSchema),
  incidents: z.array(OpsIncidentSchema),
});
export type ContingencyOverview = z.infer<typeof ContingencyOverviewSchema>;

// ───────── Slack incoming webhook (per workspace or platform) ─────────

export const AlertChannelSchema = z.object({
  id: Id,
  label: z.string(),
  /** A test post succeeded; until then the connection is «sin verificar». */
  verified: z.boolean(),
  verifiedAt: Iso.nullable(),
  lastTestAt: Iso.nullable(),
  lastError: z.string().nullable(),
  createdAt: Iso,
});
export type AlertChannel = z.infer<typeof AlertChannelSchema>;
export const SlackAlertChannelResponseSchema = z.object({ channel: AlertChannelSchema.nullable() });

export const ConnectSlackAlertSchema = z.object({
  webhookUrl: z
    .string()
    .trim()
    .url()
    .max(500)
    .refine((u) => /^https:\/\/hooks\.slack\.com\/services\/[A-Za-z0-9/_-]+$/.test(u), 'Debe ser una URL de Incoming Webhook de Slack (https://hooks.slack.com/services/…)'),
  label: z.string().trim().min(1).max(80),
});

export const SlackTestResultSchema = z.object({ ok: z.boolean(), detail: z.string(), channel: AlertChannelSchema });

// ───────── Alert settings (designated owner + thresholds) ─────────

export const ContingencyThresholdsSchema = z.object({
  /** CRM writeback failures within the window that raise an incident. */
  crmWritebackFailures: z.number().int().min(1).max(1000).default(5),
  whatsappSendFailures: z.number().int().min(1).max(1000).default(5),
  bookingErrors: z.number().int().min(1).max(1000).default(3),
  windowMinutes: z.number().int().min(1).max(1440).default(15),
  /** Same incident is not re-notified before this many minutes. */
  cooldownMinutes: z.number().int().min(1).max(1440).default(30),
});
export type ContingencyThresholds = z.infer<typeof ContingencyThresholdsSchema>;
export const DEFAULT_CONTINGENCY_THRESHOLDS: ContingencyThresholds = ContingencyThresholdsSchema.parse({});

export const ContingencySettingsSchema = z.object({
  /** Designated human owner of the developer (WhatsApp alerts when their team must act). */
  ownerMembershipId: Id.nullable(),
  /** Per-project owner overrides. */
  projectOwners: z.array(z.object({ projectId: Id, membershipId: Id })).max(200).default([]),
  /** Approved template used outside the owner's 24 h window (one body variable: the alert text). */
  whatsappTemplate: z.object({ name: z.string().min(1).max(120), language: z.string().min(2).max(10) }).nullable().default(null),
  thresholds: ContingencyThresholdsSchema.default(DEFAULT_CONTINGENCY_THRESHOLDS),
});
export type ContingencySettings = z.infer<typeof ContingencySettingsSchema>;
export type ContingencySettingsInput = z.input<typeof ContingencySettingsSchema>;
